import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { StaticImage } from 'gatsby-plugin-image'
import { type Ref, forwardRef } from 'react'
import { FormattedMessage } from 'react-intl'
import * as SiteWideStyles from '../../styles/abstract/SiteWide.css'
import * as FooterStyles from '../../styles/foundation/Footer.css'
import { formattedMessageValues } from '../../utils/FormattedMessageValues'
import UiLink from '../ui/v1/Link'
import { LocalizedLink } from '../ui/v2/LocalizedLink'
import { FooterBottom } from './v2/FooterBottom'

type FooterProps = {
  ref: Ref<HTMLDivElement>
  globalNavigationEntity: Queries.Hygraph_GlobalNavigation
  backgroundColor?: string
  hasLocales?: boolean
  localUrls?: {
    [key: string]: string
  }
}

const Footer = ({ ref, globalNavigationEntity, backgroundColor }: FooterProps) => {
  const blank = { target: '_blank', rel: 'noopener' }
  return (
    <footer className={classNames(FooterStyles.Wrapper, backgroundColor && `${backgroundColor}`)}>
      <div className={FooterStyles.Container} data-classname="Footer-Container" ref={ref}>
        <div className={FooterStyles.Inner} data-classname="Footer-Inner">
          <section className={FooterStyles.Upper} data-classname="Footer-Upper">
            <div>
              <h2 className={FooterStyles.Upper_Heading} data-classname="Footer-Upper_Heading">
                <FormattedMessage
                  id="components.foundation.footer.upper.contact.headline"
                  defaultMessage="お問い合わせ・ご相談"
                  values={{ ...formattedMessageValues }}
                />
              </h2>
              <p className={FooterStyles.Upper_Summary} data-classname="Footer-Upper_Summary">
                <FormattedMessage
                  id="components.foundation.footer.upper.contact.summary"
                  defaultMessage="貴社の業界や課題に沿って、事例やデモのご案内・お見積り等、ご不明点についてご説明いたします。まずは情報交換からお気軽にご相談ください。"
                  values={{ ...formattedMessageValues }}
                />
              </p>
              <div className={FooterStyles.Button_Container} data-classname="Footer-Button_Container">
                <div>
                  <LocalizedLink
                    to="/enterprise/"
                    target="_blank"
                    rel="noopener"
                    className={classNames(
                      FooterStyles.Button,
                      SiteWideStyles.Button({
                        color: 'white',
                        mobileSize: 'small',
                        desktopSize: 'small',
                      })
                    )}
                    data-classname="Footer-Button"
                  >
                    <FormattedMessage
                      id="components.foundation.footer.upper.contact.button1"
                      defaultMessage="お問い合わせ"
                      values={{ ...formattedMessageValues }}
                    />
                  </LocalizedLink>
                </div>
                <div>
                  <a
                    href="https://plaid.eeasy.jp/demo_KARTE_60min"
                    target="_blank"
                    rel="noreferrer noopener"
                    className={classNames(
                      FooterStyles.Button,
                      SiteWideStyles.Button({
                        color: 'white',
                        mobileSize: 'small',
                        desktopSize: 'small',
                      })
                    )}
                    data-classname="Footer-Button"
                  >
                    <FormattedMessage
                      id="components.foundation.footer.upper.contact.button2"
                      defaultMessage="オンライン相談のご予約"
                      values={{ ...formattedMessageValues }}
                    />
                  </a>
                </div>
              </div>
            </div>
            <div>
              <h2 className={FooterStyles.Upper_Heading} data-classname="Footer-Upper_Heading">
                <FormattedMessage
                  id="components.foundation.footer.upper.download.headline"
                  defaultMessage="資料ダウンロード"
                  values={{ ...formattedMessageValues }}
                />
              </h2>
              <p className={FooterStyles.Upper_Summary} data-classname="Footer-Upper_Summary">
                <FormattedMessage
                  id="components.foundation.footer.upper.download.summary"
                  defaultMessage="KARTEのプロダクト概要や導入実績、業界別の事例をまとめたPDF資料です。社内検討にお役立てください。"
                  values={{ ...formattedMessageValues }}
                />
              </p>
              <div className={FooterStyles.Button_Container} data-classname="Footer-Button_Container">
                <div>
                  <LocalizedLink
                    to="/resource/ebook/"
                    target="_blank"
                    rel="noopener"
                    className={classNames(
                      FooterStyles.Button,
                      SiteWideStyles.Button({
                        color: 'white',
                        mobileSize: 'small',
                        desktopSize: 'small',
                      })
                    )}
                    data-classname="Footer-Button"
                  >
                    <FormattedMessage
                      id="components.foundation.footer.upper.download.button"
                      defaultMessage="資料一覧を見る"
                      values={{ ...formattedMessageValues }}
                    />
                  </LocalizedLink>
                </div>
              </div>
            </div>
          </section>
          <span className={FooterStyles.Separate} data-classname="Footer-Separate" />
          <section className={FooterStyles.Lower} data-classname="Footer-Lower">
            <UiLink
              to="/"
              data-category="header"
              data-action="logo"
              data-label="KARTE"
              className={FooterStyles.Logo}
              data-classname="Footer-Logo"
            >
              <StaticImage src="../../images/common/logo/white.png" layout="fullWidth" alt="KARTEロゴ" />
            </UiLink>
            <div className={FooterStyles.Information_Container} data-classname="Footer-Information_Container">
              <div className={FooterStyles.Information} data-classname="Footer-Information">
                <div>
                  <h3 className={FooterStyles.Lower_Headline} data-classname="Footer-Lower_Headline">
                    <FormattedMessage
                      id="components.foundation.footer.upper.package.headline"
                      defaultMessage="パッケージ"
                      values={{ ...formattedMessageValues }}
                    />
                  </h3>
                  <ul className={FooterStyles.Catalog} data-classname="Footer-Catalog">
                    {globalNavigationEntity.package?.linkListing.map((packageItem: any, index: number) => (
                      <li
                        className={FooterStyles.Catalog_Item}
                        data-classname="Footer-Catalog_Item"
                        key={`packageItem_${index}`}
                      >
                        <LocalizedLink
                          to={packageItem.link}
                          {...(packageItem.isBlank && blank)}
                          className={FooterStyles.Information_Link}
                        >
                          {packageItem.name}
                          {packageItem.isBlank && (
                            <FontAwesomeIcon
                              icon={faArrowUpRightFromSquare}
                              width="8"
                              className={FooterStyles.Catalog_Icon}
                              data-classname="Footer-Catalog_Icon"
                            />
                          )}
                        </LocalizedLink>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className={FooterStyles.Lower_Headline} data-classname="Footer-Lower_Headline">
                    <LocalizedLink to={String(globalNavigationEntity.industry?.indexLink)}>
                      <FormattedMessage
                        id="components.foundation.footer.upper.industry.headline"
                        defaultMessage="事例"
                        values={{ ...formattedMessageValues }}
                      />
                    </LocalizedLink>
                  </h3>
                  <ul className={FooterStyles.Catalog} data-classname="Footer-Catalog">
                    {globalNavigationEntity.industry?.linkListing.map((industryItem: any, index: number) => (
                      <li
                        className={FooterStyles.Catalog_Item}
                        data-classname="Footer-Catalog_Item"
                        key={`industryItem_${index}`}
                      >
                        <LocalizedLink
                          to={industryItem.link}
                          {...(industryItem.isBlank && blank)}
                          className={FooterStyles.Information_Link}
                        >
                          {industryItem.label}
                          {industryItem.isBlank && (
                            <FontAwesomeIcon
                              icon={faArrowUpRightFromSquare}
                              width="8"
                              className={FooterStyles.Catalog_Icon}
                              data-classname="Footer-Catalog_Icon"
                            />
                          )}
                        </LocalizedLink>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className={FooterStyles.Information} data-classname="Footer-Information">
                <div>
                  <h3 className={FooterStyles.Lower_Headline} data-classname="Footer-Lower_Headline">
                    <a href={String(globalNavigationEntity.seminar?.indexLink)}>
                      <FormattedMessage
                        id="components.foundation.footer.upper.seminar.headline"
                        defaultMessage="セミナー"
                        values={{ ...formattedMessageValues }}
                      />
                    </a>
                  </h3>
                  <ul className={FooterStyles.Catalog} data-classname="Footer-Catalog">
                    {globalNavigationEntity.seminar?.linkListing.map((seminarItem: any, index: number) => (
                      <li
                        className={FooterStyles.Catalog_Item}
                        data-classname="Footer-Catalog_Item"
                        key={`seminarItem_${index}`}
                      >
                        <LocalizedLink
                          to={seminarItem.link}
                          {...(seminarItem.isBlank && blank)}
                          className={FooterStyles.Information_Link}
                        >
                          {seminarItem.label}
                          {seminarItem.isBlank && (
                            <FontAwesomeIcon
                              icon={faArrowUpRightFromSquare}
                              width="8"
                              className={FooterStyles.Catalog_Icon}
                              data-classname="Footer-Catalog_Icon"
                            />
                          )}
                        </LocalizedLink>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className={FooterStyles.Lower_Headline} data-classname="Footer-Lower_Headline">
                    <LocalizedLink to={String(globalNavigationEntity.ebook?.indexLink)}>
                      <FormattedMessage
                        id="components.foundation.footer.upper.ebook.headline"
                        defaultMessage="お役立ち資料"
                        values={{ ...formattedMessageValues }}
                      />
                    </LocalizedLink>
                  </h3>
                  <ul className={FooterStyles.Catalog} data-classname="Footer-Catalog">
                    {globalNavigationEntity.ebook?.linkListing.map((ebookItem: any, index: number) => (
                      <li
                        className={FooterStyles.Catalog_Item}
                        data-classname="Footer-Catalog_Item"
                        key={`ebookItem_${index}`}
                      >
                        <LocalizedLink
                          to={ebookItem.link}
                          {...(ebookItem.isBlank && blank)}
                          className={FooterStyles.Information_Link}
                        >
                          {ebookItem.label}
                          {ebookItem.isBlank && (
                            <FontAwesomeIcon
                              icon={faArrowUpRightFromSquare}
                              width="8"
                              className={FooterStyles.Catalog_Icon}
                              data-classname="Footer-Catalog_Icon"
                            />
                          )}
                        </LocalizedLink>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className={FooterStyles.Information} data-classname="Footer-Information">
                <div>
                  <h3 className={FooterStyles.Lower_Headline} data-classname="Footer-Lower_Headline">
                    <FormattedMessage
                      id="components.foundation.footer.upper.support.headline"
                      defaultMessage="サポート"
                      values={{ ...formattedMessageValues }}
                    />
                  </h3>
                  <ul className={FooterStyles.Catalog} data-classname="Footer-Catalog">
                    {globalNavigationEntity.support.map((supportItem: any, index: number) => (
                      <li
                        className={FooterStyles.Catalog_Item}
                        data-classname="Footer-Catalog_Item"
                        key={`supportItem_${index}`}
                      >
                        <LocalizedLink
                          to={supportItem.link}
                          {...(supportItem.isBlank && blank)}
                          className={FooterStyles.Information_Link}
                        >
                          {supportItem.label}
                          {supportItem.isBlank && (
                            <FontAwesomeIcon
                              icon={faArrowUpRightFromSquare}
                              width="8"
                              className={FooterStyles.Catalog_Icon}
                              data-classname="Footer-Catalog_Icon"
                            />
                          )}
                        </LocalizedLink>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className={FooterStyles.Lower_Headline} data-classname="Footer-Lower_Headline">
                    <FormattedMessage
                      id="components.foundation.footer.upper.operationalSupport.headline"
                      defaultMessage="運用支援"
                      values={{ ...formattedMessageValues }}
                    />
                  </h3>
                  <ul className={FooterStyles.Catalog} data-classname="Footer-Catalog">
                    {globalNavigationEntity.operationalSupport?.linkListing.map(
                      (operationalSupportItem: any, index: number) => (
                        <li
                          className={FooterStyles.Catalog_Item}
                          data-classname="Footer-Catalog_Item"
                          key={`supportItem_${index}`}
                        >
                          <LocalizedLink
                            to={operationalSupportItem.link}
                            {...(operationalSupportItem.isBlank && blank)}
                            className={FooterStyles.Information_Link}
                          >
                            {operationalSupportItem.label}
                            {operationalSupportItem.isBlank && (
                              <FontAwesomeIcon
                                icon={faArrowUpRightFromSquare}
                                width="8"
                                className={FooterStyles.Catalog_Icon}
                                data-classname="Footer-Catalog_Icon"
                              />
                            )}
                          </LocalizedLink>
                        </li>
                      )
                    )}
                  </ul>
                </div>
                <div>
                  <h3 className={FooterStyles.Lower_Headline} data-classname="Footer-Lower_Headline">
                    <FormattedMessage
                      id="components.foundation.footer.upper.spec.headline"
                      defaultMessage="スペック"
                      values={{ ...formattedMessageValues }}
                    />
                  </h3>
                  <ul className={FooterStyles.Catalog} data-classname="Footer-Catalog">
                    {globalNavigationEntity.spec.map((specItem: any, index: number) => (
                      <li
                        className={FooterStyles.Catalog_Item}
                        data-classname="Footer-Catalog_Item"
                        key={`specItem_${index}`}
                      >
                        <LocalizedLink
                          to={specItem.link}
                          {...(specItem.isBlank && blank)}
                          className={FooterStyles.Information_Link}
                        >
                          {specItem.label}
                          {specItem.isBlank && (
                            <FontAwesomeIcon
                              icon={faArrowUpRightFromSquare}
                              width="8"
                              className={FooterStyles.Catalog_Icon}
                              data-classname="Footer-Catalog_Icon"
                            />
                          )}
                        </LocalizedLink>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      <FooterBottom />
    </footer>
  )
}

export default forwardRef<HTMLDivElement, FooterProps>((props, ref) => {
  return Footer({
    globalNavigationEntity: props.globalNavigationEntity,
    backgroundColor: props.backgroundColor,
    ref,
  })
})
